/**
 * serviceWorker.ts - Service Worker registration and management
 * 
 * CHANGE HISTORY:
 * - 251113_2210 [pending] danielbeer: Created Service Worker registration utilities
 * 
 * Handles registration, updates, and communication with the Service Worker.
 */

export interface ServiceWorkerConfig {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onError?: (error: Error) => void;
}

/**
 * Register the Service Worker
 */
export async function registerServiceWorker(config: ServiceWorkerConfig = {}): Promise<ServiceWorkerRegistration | null> {
  // Check if Service Workers are supported
  if (!('serviceWorker' in navigator)) {
    console.warn('[SW] Service Workers not supported in this browser');
    return null;
  }

  // Don't register in development (causes issues with HMR)
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    console.log('[SW] Skipping Service Worker registration in development');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });

    console.log('[SW] Service Worker registered:', registration.scope);

    // Check for updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) return;

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // New Service Worker available
          console.log('[SW] New Service Worker available');
          config.onUpdate?.(registration);
        }
      });
    });

    // Success callback
    config.onSuccess?.(registration);

    return registration;
  } catch (error) {
    console.error('[SW] Service Worker registration failed:', error);
    config.onError?.(error as Error);
    return null;
  }
}

/**
 * Unregister the Service Worker
 */
export async function unregisterServiceWorker(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const success = await registration.unregister();
    console.log('[SW] Service Worker unregistered:', success);
    return success;
  } catch (error) {
    console.error('[SW] Service Worker unregistration failed:', error);
    return false;
  }
}

/**
 * Clear all caches
 */
export async function clearCaches(): Promise<void> {
  if (!('caches' in window)) {
    return;
  }

  try {
    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames.map(name => caches.delete(name))
    );
    console.log('[SW] All caches cleared');
  } catch (error) {
    console.error('[SW] Cache clearing failed:', error);
  }
}

/**
 * Get cache statistics
 */
export async function getCacheStats(): Promise<Record<string, any>> {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
    return {};
  }

  return new Promise((resolve) => {
    const messageChannel = new MessageChannel();
    
    messageChannel.port1.onmessage = (event) => {
      resolve(event.data);
    };

    navigator.serviceWorker.controller!.postMessage(
      { type: 'GET_CACHE_STATS' },
      [messageChannel.port2]
    );

    // Timeout after 5 seconds
    setTimeout(() => resolve({}), 5000);
  });
}

/**
 * Skip waiting and activate new Service Worker immediately
 */
export async function skipWaiting(): Promise<void> {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
    return;
  }

  navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
}

/**
 * Check if Service Worker is active
 */
export function isServiceWorkerActive(): boolean {
  return 'serviceWorker' in navigator && !!navigator.serviceWorker.controller;
}

/**
 * Listen for Service Worker updates
 */
export function onServiceWorkerUpdate(callback: () => void): () => void {
  if (!('serviceWorker' in navigator)) {
    return () => {};
  }

  const handleUpdate = () => {
    callback();
  };

  navigator.serviceWorker.addEventListener('controllerchange', handleUpdate);

  return () => {
    navigator.serviceWorker.removeEventListener('controllerchange', handleUpdate);
  };
}
